import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import { cn } from '@otovo/shared/lib/classNames';
import { Otovo$Locale } from '@otovo/shared/types/otovoweb';
import { Sanity$VerticalListOfFeatures } from '@otovo/shared/types/sanityTypes';
import { PortableText } from '@portabletext/react';
import { stegaClean } from 'next-sanity';
import { useRouter } from 'next/router';
import PortableTextComponents, {
  PortablePTag,
} from '../../Sanity/PortableTextComponents';
import CTAButton from '../../v2/CTAButton';
import { routeToHref } from '../routeToHrefUtil';

type Props = {
  content: Sanity$VerticalListOfFeatures;
};

const VerticalListOfFeatures = ({ content }: Props) => {
  const config = useGlobalConfig();
  const { locale } = useRouter() as { locale: Otovo$Locale };
  return (
    <div
      className={cn(
        'bg-surface',
        stegaClean(content.colorScheme) === 'dark'
          ? 'color-scheme-dark'
          : 'color-scheme-white',
      )}
      data-testid="vertical-list-of-features"
    >
      <div className="mx-auto max-w-8xl px-4 py-16 md:px-6 md:py-20 lg:px-16 lg:py-24">
        <div className="grid-cols-12 md:grid">
          <div className="col-span-9 mb-20 space-y-6 md:mb-12 lg:col-span-5 lg:mb-0 lg:pr-10 xl:col-span-4">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-pretty text-heading md:text-3xl lg:text-4xl">
                {content.heading}
              </h2>
              <PortablePTag
                className="text-lg text-subheading md:text-xl"
                value={content.description}
              />
            </div>
            {content.cta && (
              <div className="mt-6">
                <CTAButton
                  href={routeToHref(
                    content.cta.route,
                    config.BU_CONFIG.market,
                    locale,
                  )}
                >
                  {content.cta.title}
                </CTAButton>
              </div>
            )}
          </div>
          <div className="col-span-10 space-y-6 lg:col-span-5 lg:col-start-7 xl:col-start-8">
            <ol className="space-y-6 divide-y divide-heading/5">
              {content.features.map((step, index) => (
                <li
                  key={step.title}
                  className="relative grid grid-cols-[auto_1fr] gap-4 pb-8"
                >
                  {content.numberedList && (
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-xl bg-heading/5 font-semibold text-heading"
                      aria-hidden="true"
                    >
                      {index + 1}
                    </div>
                  )}
                  <div>
                    <h3 className="mb-3 block text-xl font-semibold text-balance text-subheading md:text-2xl">
                      {step.title}
                    </h3>
                    <PortableText
                      value={step.description}
                      components={PortableTextComponents}
                    />
                  </div>
                </li>
              ))}
            </ol>
            {content.footer && (
              <div className="border-y border-heading/5 py-6 text-muted">
                <p>{content.footer}</p>
              </div>
            )}
          </div>
          {content.image && (
            <div className="relative col-span-12 mt-10 mb-10 hidden overflow-hidden md:block">
              <SanityImage
                maxHeightFull
                image={content.image}
                className="mr-auto aspect-[21/9] h-full w-full rounded-xl object-cover shadow-2xl ring-1 ring-grey-90/10"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerticalListOfFeatures;
